import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight, CircleHelp, Minus, X } from 'lucide-react';
import clsx from 'clsx';
import { get } from '../lib/api';
import { ErrorNote, Loading } from '../ui';

/**
 * The account judged against the claude-ads Google control catalog, on its
 * scoring contract: one number, six weighted categories, and every control
 * with what it found and what to do.
 *
 * The number never appears without its coverage, and under 60% it does not
 * appear as the account's health at all — the contract withholds it. There
 * is no letter grade: the contract forbids one.
 */

type Outcome = 'pass' | 'fail' | 'unknown' | 'not_applicable';
type Severity = 'critical' | 'high' | 'medium';
export type Grade = 'Star' | 'Good' | 'Costly' | 'Bleeding' | 'Watch' | 'Too early' | 'Idle' | 'Unmeasured' | 'Unknown';
type Check = {
  id: string; control: string | null; category: string; severity: Severity; weight: number; title: string; outcome: Outcome;
  detail: string; fix: string | null; at_risk: number | null; evidence: Record<string, unknown>[];
};
type Card = {
  health: number | null; health_raw: number | null; coverage: number;
  status: 'graded' | 'provisional' | 'insufficient_evidence';
  basis: { spend: number; conversions: number; value: number; mode: 'roas' | 'cpa' | 'none'; cpa: number | null; roas: number | null };
  categories: { key: string; label: string; weight: number; health: number | null; coverage: number | null; passed: number; failed: number; unknown: number; applicable: number }[];
  priority: string[];
  checks: Check[];
  marks: Record<string, { counts: Partial<Record<Grade, number>>; bleeding_cost: number }>;
};

/** Status tokens, and always the word with them — never colour alone. */
export const GRADE_STYLE: Record<Grade, string> = {
  Star: 'bg-ok-bg text-ok',
  Good: 'bg-ok-bg text-ok',
  Watch: 'bg-warn-bg text-warn',
  Costly: 'bg-warn-bg text-warn',
  Bleeding: 'bg-err-bg text-err',
  'Too early': 'bg-bg3 text-t3',
  Idle: 'bg-bg3 text-t3',
  Unmeasured: 'bg-bg3 text-t3',
  Unknown: 'bg-bg3 text-t3',
};
export const GRADE_MEANING: Record<Grade, string> = {
  Star: 'Converts at least 1.4× as efficiently as the account average, on 2+ conversions',
  Good: 'Converts at or around the account average',
  Costly: 'Each conversion costs more than 1.5× the account average',
  Bleeding: 'Spent more than 1.5 conversions\' worth with no conversion',
  Watch: 'Spent half a conversion\'s worth or more with none yet',
  'Too early': 'Too little spend to judge',
  Idle: 'Did nothing in this window',
  Unmeasured: 'Spent, but the account records no conversions to judge against',
  Unknown: 'Google did not give the numbers',
};
export const GradeBadge = ({ grade }: { grade: Grade | null }) => grade
  ? <span title={GRADE_MEANING[grade]} className={clsx('inline-flex whitespace-nowrap rounded-chip px-2 py-0.5 text-xs font-medium', GRADE_STYLE[grade])}>{grade}</span>
  : <span className="text-t3">—</span>;

const OUTCOME = {
  fail: { label: 'Fails', icon: X, cls: 'text-err' },
  unknown: { label: 'Could not be judged — data not read', icon: CircleHelp, cls: 'text-warn' },
  pass: { label: 'Passes', icon: Check, cls: 'text-ok' },
  not_applicable: { label: 'Does not apply to this account', icon: Minus, cls: 'text-t3' },
} as const;

const SEVERITY: Record<Severity, string> = {
  critical: 'bg-err-bg text-err',
  high: 'bg-warn-bg text-warn',
  medium: 'bg-bg3 text-t2',
};

const STATUS: Record<Card['status'], { label: string; cls: string; note: string }> = {
  graded: { label: 'Graded', cls: 'bg-ok-bg text-ok', note: 'Enough of the catalog could be judged to state this as the account\'s health.' },
  provisional: { label: 'Provisional', cls: 'bg-warn-bg text-warn', note: 'Only 60–80% of the catalog could be judged. Say it as provisional.' },
  insufficient_evidence: { label: 'Insufficient evidence', cls: 'bg-err-bg text-err', note: 'Under 60% of the catalog could be judged, so no health figure is stated for this account.' },
};

const SECTION_LABEL: Record<string, string> = {
  campaigns: 'Campaigns', ad_groups: 'Ad groups', keywords: 'Keywords', search_terms: 'Search terms', ads: 'Ads',
};

export function Scorecard({ snapshotId, currency, onOpen }: {
  snapshotId: string; currency: string; onOpen: (section: string, grade?: Grade) => void;
}) {
  const q = useQuery({ queryKey: ['score', snapshotId], queryFn: () => get<Card>(`/v1/mirror/${snapshotId}/score`) });
  const money = (v: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);

  if (q.isLoading) return <Loading label="Scoring" />;
  if (q.error || !q.data) return <ErrorNote error={q.error} />;
  const c = q.data;
  const groups = (['fail', 'unknown', 'pass', 'not_applicable'] as Outcome[]).map((o) => ({ o, list: c.checks.filter((x) => x.outcome === o) }));
  // Controls overlap on purpose (a junk search is also a non-converting one),
  // so the sum is capped at what the account actually spent — a figure
  // larger than the spend is the overstatement that gets an audit argued down.
  const atRisk = Math.min(c.basis.spend, c.checks.filter((x) => x.outcome === 'fail').reduce((t, x) => t + (x.at_risk ?? 0), 0));

  return (
    <div className="space-y-4">
      {/* ── the number, and what it rests on ─────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <div className="rounded-card border border-bd bg-bg2 p-5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-t3">Account health</span>
            <span className={clsx('ml-auto rounded-chip px-2 py-0.5 text-xs font-medium', STATUS[c.status].cls)}>{STATUS[c.status].label}</span>
          </div>
          {c.health != null ? (
            <div className="mt-1 flex items-baseline gap-2">
              <span className="num text-6xl font-bold tracking-tight">{c.health}</span>
              <span className="text-2xl font-bold text-t2">/ 100</span>
            </div>
          ) : (
            <p className="mt-3 text-sm text-t2">
              {c.health_raw == null ? 'Nothing could be judged — see the controls below for why.'
                : <>Withheld. <span className="text-t3">(For the team only: the judged part scores {c.health_raw}.)</span></>}
            </p>
          )}
          <div className="mt-3">
            <div className="flex justify-between text-xs text-t2">
              <span>Evidence coverage</span><b className="num text-t1">{Math.round(c.coverage * 100)}%</b>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-bg3" role="img" aria-label={`Coverage ${Math.round(c.coverage * 100)}%`}>
              <span className="block h-full rounded-full bg-ink" style={{ width: `${Math.round(c.coverage * 100)}%` }} />
            </div>
            <p className="mt-1.5 text-xs text-t3">{STATUS[c.status].note}</p>
          </div>
          <div className="mt-4 border-t border-bd pt-3 text-xs text-t2">
            {c.basis.mode === 'none'
              ? 'The account records no conversions, so no row can be judged better or worse than another yet.'
              : c.basis.mode === 'roas'
                ? <>Every row is judged against this account&apos;s own return: <b className="num text-t1">{c.basis.roas}×</b> conversion value per rupee.</>
                : <>Every row is judged against this account&apos;s own average: <b className="num text-t1">{money(c.basis.cpa!)}</b> per conversion.</>}
          </div>
          {atRisk > 0 && (
            <div className="mt-3 rounded-btn bg-err-bg px-3 py-2 text-xs text-err">
              <b className="num">{money(atRisk)}</b> of this window&apos;s spend sits where a failing control says it should not. Controls overlap, so this is an upper bound, capped at the spend.
            </div>
          )}
        </div>

        <div className="rounded-card border border-bd bg-bg2 p-5">
          <div className="mb-3 flex items-baseline gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-t3">By category</span>
            <span className="text-xs text-t3">· weighted by Google&apos;s reference profile</span>
          </div>
          <ul className="space-y-2.5">
            {c.categories.map((a) => (
              <li key={a.key} className="grid grid-cols-[minmax(0,180px)_minmax(0,1fr)_92px] items-center gap-3 text-sm">
                <span className="truncate">{a.label} <span className="num text-xs text-t3">{a.weight}%</span></span>
                <span className="h-2 overflow-hidden rounded-full bg-bg3" role="img" aria-label={`${a.label}: ${a.health ?? 'not judged'}`}>
                  {a.health != null && <span className="block h-full rounded-full bg-ink" style={{ width: `${Math.max(2, a.health)}%` }} />}
                </span>
                <span className="num text-right text-xs">
                  {a.health == null ? <span className="text-t3">not judged</span>
                    : <><b>{a.health}</b><span className="text-t3"> · {a.passed}/{a.passed + a.failed}{a.unknown ? ` · ${a.unknown}?` : ''}</span></>}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-t3">
            Controls from the claude-ads Google catalog. Critical weighs 5, high 3, medium 1. <b className="font-medium">?</b> = could not be judged.
          </p>
        </div>
      </div>

      {/* ── every row, marked ────────────────────────────────────────────── */}
      <div className="rounded-card border border-bd bg-bg2 p-5">
        <div className="mb-3 flex flex-wrap items-baseline gap-2">
          <h2 className="text-sm font-bold">Every row, marked</h2>
          <span className="text-xs text-t3">Click a mark to see those rows.</span>
        </div>
        <div className="space-y-3">
          {Object.entries(SECTION_LABEL).map(([key, label]) => {
            const m = c.marks[key];
            if (!m) return null;
            const order: Grade[] = ['Bleeding', 'Costly', 'Watch', 'Good', 'Star', 'Too early', 'Idle', 'Unmeasured', 'Unknown'];
            return (
              <div key={key} className="flex flex-wrap items-center gap-2 text-sm">
                <button onClick={() => onOpen(key)} className="w-28 text-left font-medium hover:underline">{label}</button>
                {order.filter((g) => m.counts[g]).map((g) => (
                  <button key={g} onClick={() => onOpen(key, g)} title={GRADE_MEANING[g]}
                    className={clsx('rounded-chip px-2 py-0.5 text-xs font-medium hover:opacity-80', GRADE_STYLE[g])}>
                    {g} <span className="num">{m.counts[g]}</span>
                  </button>
                ))}
                {m.bleeding_cost > 0 && <span className="num text-xs text-err">· {money(m.bleeding_cost)} on Bleeding</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── the rulebook ─────────────────────────────────────────────────── */}
      {groups.map(({ o, list }) => list.length > 0 && (
        <CheckGroup key={o} outcome={o} checks={list} categories={c.categories} money={money} startOpen={o === 'fail' || o === 'unknown'} />
      ))}
    </div>
  );
}

function CheckGroup({ outcome, checks, categories, money, startOpen }: {
  outcome: Outcome; checks: Check[]; categories: Card['categories']; money: (v: number) => string; startOpen: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  const O = OUTCOME[outcome];
  return (
    <div className="rounded-card border border-bd bg-bg2">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-5 py-3 text-left">
        {open ? <ChevronDown className="size-4 text-t3" /> : <ChevronRight className="size-4 text-t3" />}
        <O.icon className={clsx('size-4', O.cls)} />
        <span className="text-sm font-bold">{O.label}</span>
        <span className="num text-xs text-t3">{checks.length}</span>
      </button>
      {open && (
        <ul>
          {checks.map((x) => <CheckRow key={x.id} x={x} area={categories.find((a) => a.key === x.category)?.label ?? x.category} money={money} />)}
        </ul>
      )}
    </div>
  );
}

function CheckRow({ x, area, money }: { x: Check; area: string; money: (v: number) => string }) {
  const [more, setMore] = useState(false);
  const O = OUTCOME[x.outcome];
  const cols = x.evidence.length ? Object.keys(x.evidence[0]) : [];
  return (
    <li className="border-t border-bd px-5 py-3">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
        <O.icon className={clsx('mt-0.5 size-4 shrink-0', O.cls)} aria-label={O.label} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            {x.control && <span className="num rounded-chip bg-bg3 px-1.5 text-xs text-t2" title="claude-ads catalog ID">{x.control}</span>}
            <span className="text-sm font-medium">{x.title}</span>
            <span className={clsx('rounded-chip px-1.5 text-xs', SEVERITY[x.severity])} title={`Weighs ${x.weight} in its category`}>{x.severity}</span>
            <span className="text-xs text-t3">{area}</span>
            {x.at_risk != null && x.at_risk > 0 && x.outcome !== 'pass' && (
              <span className="num ml-auto text-sm font-bold text-err">{money(x.at_risk)}</span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-t2">{x.detail}</p>
          {x.fix && x.outcome === 'fail' && (
            <p className="mt-1 text-sm"><b className="font-medium">Fix: </b>{x.fix}</p>
          )}
          {x.evidence.length > 0 && x.outcome !== 'pass' && (
            <button onClick={() => setMore(!more)} className="mt-1 text-xs text-t2 underline hover:text-t1">
              {more ? 'Hide' : `Show the ${x.evidence.length} row${x.evidence.length === 1 ? '' : 's'} behind this`}
            </button>
          )}
          {more && (
            <div className="mt-2 overflow-x-auto rounded-btn border border-bd">
              <table className="w-full text-xs">
                <thead className="bg-bg3 text-left text-t2">
                  <tr>{cols.map((k) => <th key={k} className="whitespace-nowrap px-2 py-1.5 font-medium">{k.replace(/_/g, ' ')}</th>)}</tr>
                </thead>
                <tbody>
                  {x.evidence.map((e, i) => (
                    <tr key={i} className="border-t border-bd">
                      {cols.map((k) => {
                        const v = e[k];
                        const text = v == null ? '—' : Array.isArray(v) ? v.join(', ')
                          : typeof v === 'number' && /cost|spend/.test(k) ? money(v) : String(v);
                        return <td key={k} className={clsx('px-2 py-1.5', typeof v === 'number' && 'num text-right')}>{text}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}
